import React, { useRef, useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Form, Button } from "antd"
import { v4 as uuidv4 } from "uuid"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { sendMessage, updateMessage, markMessageFailed, addLocalMessage, typingSelector, clearEditingMessage } from "store/conversations"
import { Icon } from "ui/Icon"
import MessageEditor from "components/messenger/ProMessageEditor"
import styles from "./ChatHistory.module.css"

export const MessageForm = ({ conversation, subscription }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const [media, setMedia] = useState(null)
  const [messageValue, setMessageValue] = useState("") // Local state for message value
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)
  const typingData = useSelector(typingSelector(conversation.id))
  const role = useSelector((state) => state.user.role)
  const isGuide = role === "guide_user"
  const typingUsers = Object.values(typingData)
    .filter((user) => user.isTyping && (isGuide ? user.user_id !== conversation.contact_id : user.user_id !== conversation.client_id))
    .map((user) => user.userName)
  const user = isGuide ? conversation.contact : conversation.client

  const timestamp = new Date().toISOString()
  const dateOnly = timestamp.split("T")[0]
  const editingState = useSelector((state) => ({
    editingMessageId: state.conversations.editingMessageId,
    editingMessageBody: state.conversations.editingMessageBody
  }))
  const { editingMessageId, editingMessageBody } = editingState

  useEffect(() => {
    if (editingMessageBody) {
      setMessageValue(editingMessageBody)
    }
  }, [editingMessageBody])

  const createOptimisticMessage = (tempId, file = null, index = null) => ({
    id: index !== null ? `${tempId}-${index}` : tempId,
    uuid: index !== null ? `${tempId}-${index}` : tempId,
    body: messageValue,
    author: user,
    created_at: timestamp,
    updated_at: timestamp,
    viewed: false,
    conversation_id: conversation.id,
    author_id: user?.id,
    media_url: file?.url || null,
    media_filename: file?.filename || null,
    media_type: file?.type || null,
    media: file?.file || null,
    isFirstMessageOfNewDay: false,
    nestedMessages: [],
    firstMessageOfTheDate: dateOnly,
    failed: false,
    temp: true
  })

  const onFinish = async () => {
    if (media?.length > 0) {
      setLoading(true)
    }

    form.resetFields()
    setMessageValue("")

    if (editingMessageId) {
      await handleEditMessage()
    } else {
      await handleNewMessage()
    }
  }

  const handleEditMessage = async () => {
    try {
      await dispatch(updateMessage(editingMessageId, { body: messageValue }))
      dispatch(clearEditingMessage())
    } catch (error) {
      console.error("Failed to update message:", error)
    }
  }

  const handleNewMessage = async () => {
    const tempId = uuidv4()
    if (media?.length) {
      await handleMediaMessages(tempId)
    } else {
      await handleTextMessage(tempId)
    }
  }

  const handleMediaMessages = async (tempId) => {
    for (const [index, file] of media.entries()) {
      const optimisticMessage = createOptimisticMessage(tempId, file, index)
      const formData = new FormData()
      formData.append("message[body]", messageValue || "")
      formData.append("message[media]", file.file)

      try {
        await dispatch(sendMessage(conversation.id, formData))
      } catch (error) {
        console.error("Failed to send media message:", error)
        dispatch(addLocalMessage(optimisticMessage))
        dispatch(markMessageFailed({ tempId: optimisticMessage.id }))
      }
    }
    setLoading(false)
    setMedia(null)
  }

  const handleTextMessage = async (tempId) => {
    const optimisticMessage = createOptimisticMessage(tempId)
    const formData = new FormData()
    formData.append("message[body]", messageValue || "")

    try {
      await dispatch(sendMessage(conversation.id, formData))
    } catch (error) {
      console.error("Failed to send text message:", error)
      dispatch(addLocalMessage(optimisticMessage))
      dispatch(markMessageFailed({ tempId }))
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault() // Prevent default behavior of adding a new line
      if (e.target.value) {
        onFinish()
      }
    }
  }

  const disable = !messageValue && (media?.length === 0 || media === null)

  return (
    <>
      {typingUsers.length > 0 && (
        <div className={styles.typingIndicator}>
          <span>{typingUsers.join(", ")} is typing</span>
          <span className={styles.dots}>
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </div>
      )}
      <div className={styles.footer} ref={ref}>
        <Form form={form} layout="inline" onFinish={onFinish}>
          <Form.Item name="message" style={{ flex: 1 }}>
            <MessageEditor
              ref={ref}
              type="submit"
              className="flex align-center"
              placeholder={t("messenger.placeholder")}
              uploadClassName={styles.upload}
              inputClassName={styles.input}
              imageContainer={styles.imageContainer}
              subscription={subscription}
              conversation={conversation}
              user={user}
              media={media}
              loading={loading}
              setMedia={setMedia}
              messageValue={messageValue}
              setMessageValue={setMessageValue}
              onSubmit={onFinish}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <Form.Item className="ml-s">
            <Button
              icon={<Icon name="Send" color="#ffffff" width={16} height={16} />}
              type="primary"
              htmlType="submit"
              disabled={disable}
              className={disable ? styles.disabledButton : ""}
            />
          </Form.Item>
        </Form>
      </div>
    </>
  )
}

MessageForm.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    client_id: PropTypes.string.isRequired,
    contact: PropTypes.object,
    client: PropTypes.object
  }).isRequired,
  subscription: PropTypes.object
}
